import { z } from 'zod';
// ─── Dashboards (DEV: AI-managed dashboards) ─────────────────────────────
//
// Dashboards are NOT human-edited: they are created and updated by
// orchestration agents (dashboard_* tools injected by the PA host when the
// definition's `dashboardsAllowed` is on) and by external MCP callers (PAT
// via mcp-server). The console renders them read-only. Each widget is one
// self-contained index.html rendered in a sandboxed iframe (srcdoc, opaque
// origin) whose only capability is the postMessage bridge — through which it
// may call the annotated MCP tools declared in its `toolAllowlist`.
/** Enforced cap on one widget's HTML source (bytes). */
export const dashboardWidgetHtmlMaxBytes = 1_048_576; // 1 MiB
/** Caps mirrored by service-layer validation. */
export const dashboardGridMaxCols = 24;
export const dashboardGridMaxRows = 24;
export const dashboardMaxWidgets = 24;
export const dashboardWidgetMaxAllowlist = 50;
export const DashboardKindSchema = z.enum(['single', 'grid']);
/**
 * One bridge-callable tool grant: the widget may invoke `toolName` on the
 * given MCP connection. Only tools with an org-authored output annotation
 * (`MCPToolAnnotation`) are grantable — validated at write time and
 * re-checked by the console bridge at call time.
 */
export const DashboardToolGrantSchema = z.object({
    mcpConnectionId: z.string().min(1),
    toolName: z.string().min(1).max(200),
});
/** Widget metadata — everything except the HTML body (listings stay cheap). */
export const DashboardWidgetMetaSchema = z.object({
    id: z.string(),
    dashboardId: z.string(),
    title: z.string(),
    /** Byte size of the HTML source. */
    size: z.number().int().nonnegative(),
    /** Placement on the dashboard grid (ignored for kind=single). */
    x: z.number().int().nonnegative(),
    y: z.number().int().nonnegative(),
    w: z.number().int().positive(),
    h: z.number().int().positive(),
    toolAllowlist: z.array(DashboardToolGrantSchema),
    /** Who last wrote it: 'agent' (a thread id) | 'user' (an auth subject). */
    updatedByType: z.enum(['agent', 'user']).nullable(),
    updatedById: z.string().nullable(),
    createdAt: z.date(),
    /** Doubles as the optimistic-concurrency version token for writes. */
    updatedAt: z.date(),
});
/** Full widget — metadata plus the HTML source (the read half of the edit loop). */
export const DashboardWidgetSchema = DashboardWidgetMetaSchema.extend({
    html: z.string(),
});
export const DashboardSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    kind: DashboardKindSchema,
    /** Grid geometry (grid kind only): the logical canvas widgets are placed on. */
    gridCols: z.number().int().positive(),
    gridRows: z.number().int().positive(),
    /**
     * Which widget fills the page when kind=single; null = first widget.
     * Widgets keep their grid placement either way, so toggling kinds is
     * lossless.
     */
    primaryWidgetId: z.string().nullable(),
    /** Who created it: 'agent' (a thread id) | 'user' (an auth subject). */
    createdByType: z.enum(['agent', 'user']).nullable(),
    createdById: z.string().nullable(),
    createdAt: z.date(),
    updatedAt: z.date(),
    widgets: z.array(DashboardWidgetMetaSchema),
});
/** List row — widget count instead of the widget array. */
export const DashboardListItemSchema = DashboardSchema.omit({ widgets: true }).extend({
    widgetCount: z.number().int().nonnegative(),
    /**
     * Whether the CALLING user starred this dashboard (drives the nav rail's
     * Favorites folder + the star on the All dashboards page). Optional so
     * responses from servers predating favorites parse.
     */
    isFavorite: z.boolean().optional(),
    /**
     * Derived: the latest content change across the dashboard row AND its
     * widgets (max updatedAt). Distinct from `updatedAt`, which is the
     * dashboard row's own optimistic-concurrency token and does NOT move when
     * a widget is edited. Drives the nav rail's unseen-update shine. Optional
     * so responses from servers predating it parse.
     */
    contentUpdatedAt: z.date().optional(),
});
// ─── HTTP surface ─────────────────────────────────────────────────────────
export const CreateDashboardInputSchema = z.object({
    name: z.string().min(1).max(200),
    description: z.string().max(2000).optional(),
    kind: DashboardKindSchema,
    gridCols: z.number().int().positive().max(dashboardGridMaxCols).default(12),
    gridRows: z.number().int().positive().max(dashboardGridMaxRows).default(8),
});
export const UpdateDashboardInputSchema = CreateDashboardInputSchema.partial().extend({
    // null falls back to "first widget"; undefined leaves unchanged.
    primaryWidgetId: z.string().nullable().optional(),
});
/** Body for `POST /v1/dashboards/:id/widgets` — create a widget. */
export const CreateDashboardWidgetInputSchema = z.object({
    title: z.string().min(1).max(200),
    html: z.string().max(dashboardWidgetHtmlMaxBytes),
    x: z.number().int().nonnegative().default(0),
    y: z.number().int().nonnegative().default(0),
    w: z.number().int().positive().default(12),
    h: z.number().int().positive().default(8),
    toolAllowlist: z.array(DashboardToolGrantSchema).max(dashboardWidgetMaxAllowlist).default([]),
});
/**
 * Body for `PUT /v1/dashboards/:id/widgets/:widgetId` — partial update; only
 * the fields sent change. `ifUnmodifiedSince` is the `updatedAt` the caller
 * read (ISO string); a mismatch means someone else wrote in between → 409.
 */
export const UpdateDashboardWidgetInputSchema = CreateDashboardWidgetInputSchema.partial().extend({
    ifUnmodifiedSince: z.coerce.date().optional(),
});
/**
 * Body for `PATCH /v1/dashboards/:id/layout` — batch, atomic placement
 * update. Covers whichever widgets are moving (others stay put); never
 * touches html/title/allowlist. Validated as a whole against grid bounds.
 */
export const ArrangeDashboardInputSchema = z.object({
    layout: z
        .array(z.object({
        widgetId: z.string().min(1),
        x: z.number().int().nonnegative(),
        y: z.number().int().nonnegative(),
        w: z.number().int().positive(),
        h: z.number().int().positive(),
    }))
        .min(1)
        .max(dashboardMaxWidgets),
});
/** Cap on the org's aesthetics-guidance text. */
export const dashboardGuidanceMaxLength = 8000;
/**
 * Org-wide dashboard aesthetics guidance — a human-written style brief
 * (palette, typography, tone) shown at the top of the All dashboards page
 * and read by authoring agents via `dashboard_get_guidance` before they
 * write widget HTML. `updatedAt` null = never set.
 */
export const DashboardGuidanceSchema = z.object({
    text: z.string(),
    updatedAt: z.date().nullable(),
    updatedBySubject: z.string().nullable(),
});
/** Body for `PUT /v1/dashboards/guidance`. */
export const UpdateDashboardGuidanceInputSchema = z.object({
    text: z.string().max(dashboardGuidanceMaxLength),
});
/**
 * One bridge-callable tool, as returned by `GET /v1/dashboards/callable-tools`:
 * the intersection of a connection's advertised tools (`toolSchemaCache`) and
 * the org's output annotations (`MCPToolAnnotation`). This is the catalog an
 * authoring agent consults BEFORE writing widget JS — the output schema is
 * the shape the widget may rely on — and the same catalog the console bridge
 * serves to widgets at runtime (`cortex:list-tools`).
 */
export const DashboardCallableToolSchema = z.object({
    mcpConnectionId: z.string(),
    /** Human-facing connection label (widgets/agents show it, never key on it). */
    connectionName: z.string(),
    toolName: z.string(),
    description: z.string().nullable(),
    /** JSON Schema of the tool's input, from the connection's toolSchemaCache. */
    inputSchema: z.unknown().nullable(),
    /** Org-authored expected output JSON Schema (the MCPToolAnnotation). */
    outputSchema: z.unknown(),
});
// ─── Public sharing ───────────────────────────────────────────────────────
//
// A DashboardShare's `id` IS the guid presented in the public share URL
// (`?share=<guid>`) — unlike PersonalAccessToken there is no separate
// hashed-secret column, since the id itself is the only thing granting
// access. Anonymous viewers hit a `@Public()` API surface keyed by that id;
// no bearer token is ever involved.
/** Cap on a share's optional human label (shown in the Manage Shares list). */
export const dashboardShareMaxLabelLength = 200;
export const CreateDashboardShareInputSchema = z.object({
    label: z.string().max(dashboardShareMaxLabelLength).optional(),
});
/** Management view of a share — returned by create/list, never re-fetched by guid. */
export const DashboardShareSchema = z.object({
    id: z.string(),
    dashboardId: z.string(),
    label: z.string().nullable(),
    viewCount: z.number().int().nonnegative(),
    lastViewedAt: z.date().nullable(),
    revokedAt: z.date().nullable(),
    createdAt: z.date(),
});
/**
 * The anonymous, unauthenticated read shape served at
 * `GET /v1/dashboard-shares/:shareId`. Deliberately minimal — omits actor
 * fields (`createdByType`/`createdById`/`description`) present on the
 * authenticated `DashboardSchema`, since this payload is world-readable.
 */
export const PublicDashboardViewSchema = z.object({
    name: z.string(),
    kind: DashboardKindSchema,
    gridCols: z.number().int().positive(),
    gridRows: z.number().int().positive(),
    primaryWidgetId: z.string().nullable(),
    widgets: z.array(DashboardWidgetSchema),
});
//# sourceMappingURL=dashboard.js.map